import { useState, useEffect } from "react";
import { Star, Quote, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import { apiFetch } from "@/lib/api";

interface Review {
  id: number;
  name: string;
  subtitle: string | null;
  text: string;
  rating: number;
  imagePath: string | null;
  source: string;
}

const fallbackReviews: Review[] = [
  { id: 0, name: "Catherine R.", subtitle: null, text: "The Tallahassee Lassie team takes such amazing care of our crew of animals when we are away. I highly recommend them to anyone looking for petsitting. They're the first petsitters I have found who make our time away from home LESS stressful. I never have to worry about my animals, they send updates each time they visit. There are never any issues with the care our animals receive, and it just feels awesome to be able to travel again without any stress about how our critters are back home. Can't recommend enough!", rating: 5, imagePath: null, source: "manual" },
  { id: 1, name: "Savannah P.", subtitle: null, text: "We love Tallahassee Lassie Pet Care! They took amazing care of our kitties while we were out of town, and they also brought some packages inside. Our normally shy cat even came out for some love! I love getting the photos and daily updates on them as well!", rating: 5, imagePath: null, source: "manual" },
  { id: 2, name: "William F.", subtitle: null, text: "I unexpectedly adopted two 7 week old puppies from the middle of the highway. Not prepared AT ALL for babies. Desperately reached out to Carla's team and they have helped us raise them into two wonderful adults. They truly treat your pets as if they're their own! Highly recommend.", rating: 5, imagePath: null, source: "manual" },
];

export default function Reviews() {
  const [reviews, setReviews] = useState<Review[]>(fallbackReviews);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    apiFetch("/reviews")
      .then((data) => {
        if (data && data.length > 0) {
          setReviews(data);
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  return (
    <div className="py-16 md:py-24">
      <SEO
        title="Client Reviews | Dog Walking & Pet Sitting"
        description="Dog Walking and Pet Sitting Client Reviews Tallahassee Lassie Pet Care. We provide daily dog walking in Tallahassee and many of our families have used us for multiple years."
        path="/reviews"
      />
      <div className="container mx-auto px-4">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-6">Worth Barkin About!</h1>
            <div className="flex justify-center gap-1 mb-6 text-yellow-400">
              {[...Array(5)].map((_, i) => <Star key={i} fill="currentColor" size={32} />)}
            </div>
            <p className="text-xl text-muted-foreground mb-6">
              Please visit us for more up-to-date reviews! Thank you, tell your pets we said hi!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { label: "Google", href: "https://maps.app.goo.gl/jEMkuCgt4RS2iknVA" },
                { label: "Facebook", href: "https://www.facebook.com/groups/468402496836646/user/100063715921641/" },
                { label: "NextDoor", href: "https://nextdoor.com/pages/tallahassee-lassie-tallahassee-fl/" },
                { label: "Yelp", href: "https://www.yelp.com/biz/tallahassee-lassie-pet-care-tallahassee" },
              ].map((platform) => (
                <a
                  key={platform.label}
                  href={platform.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-primary/30 text-primary font-medium text-sm hover:bg-primary hover:text-white transition-colors"
                  style={{ borderRadius: "12px 0 12px 0" }}
                >
                  {platform.label}
                  <ExternalLink size={14} />
                </a>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {reviews.map((review, index) => (
            <Reveal key={review.id} delay={index * 100}>
              <div className="bg-card border border-border/50 p-8 shadow-sm relative group card-lift h-full" style={{ borderRadius: "24px 0 24px 0" }}>
                <Quote className="absolute top-6 right-6 text-primary/10 w-12 h-12 rotate-180 group-hover:text-primary/20 transition-colors" />
                
                <div className="flex gap-1 text-yellow-400 mb-6">
                  {[...Array(review.rating)].map((_, i) => <Star key={i} fill="currentColor" size={18} />)}
                </div>
                
                <p className="text-lg italic text-foreground/80 leading-relaxed mb-8 relative z-10">
                  "{review.text}"
                </p>
                
                <div className="flex items-center gap-4 mt-auto border-t border-border/50 pt-6">
                  {review.imagePath ? (
                    <img src={review.imagePath} alt={review.subtitle || review.name} className="w-14 h-14 object-cover rounded-full border-2 border-secondary" />
                  ) : (
                    <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center text-xl">
                      🐾
                    </div>
                  )}
                  <div>
                    <h4 className="font-serif font-bold text-lg">{review.name}</h4>
                    {review.subtitle && (
                      <p className="text-sm text-muted-foreground">{review.subtitle}</p>
                    )}
                    {review.source === "google" && (
                      <p className="text-xs text-blue-600 mt-0.5">via Google</p>
                    )}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
